import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const EmploymentPage = () => {
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    positionAppliedFor: '',
    employmentType: '',
    availableStartDate: '',
    education: '',
    experience: '',
    certifications: '',
    coverLetter: '',
    resume: 'Uncollectable',
    references: ''
  });

  const [submitStatus, setSubmitStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus(null);

    try {
      const formDataEncoded = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataEncoded.append(key, String(value));
      });

      const response = await fetch('https://readdy.ai/api/form/d58v9ufahj0hkf71i8hg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataEncoded.toString()
      });

      if (response.ok) {
        setSubmitStatus({ type: 'success', message: 'Application submitted successfully! Our HR team will review your application and contact you within 5-7 business days.' });
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          address: '',
          city: '',
          state: '',
          zipCode: '',
          positionAppliedFor: '',
          employmentType: '',
          availableStartDate: '',
          education: '',
          experience: '',
          certifications: '',
          coverLetter: '',
          resume: 'Uncollectable',
          references: ''
        });
      } else {
        setSubmitStatus({ type: 'error', message: 'Submission failed. Please try again.' });
      }
    } catch (error) {
      setSubmitStatus({ type: 'error', message: 'Network error. Please try again.' });
    }
  };

  const openPositions = [
    {
      title: 'Psychiatry Intern',
      type: 'Internship',
      description: 'Gain hands-on experience in outpatient psychiatric care under supervision of licensed clinicians.',
      requirements: ['Currently enrolled in graduate program', 'Interest in mental health', 'Flexible schedule']
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Banner */}
      <section className="relative h-[40vh] flex items-center justify-center bg-gradient-to-r from-primary to-primary-dark">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLXdpZHRoPSIyIi8+PC9nPjwvc3ZnPg==')]"></div>
        </div>
        <div className="relative z-10 text-center px-6">
          <motion.h1
            className="text-5xl md:text-6xl font-bold text-white mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Join Our Team
          </motion.h1>
          <motion.p
            className="text-white/80 text-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <Link to="/" className="hover:text-white cursor-pointer">Home</Link> <span className="mx-2">/</span> Employment
          </motion.p>
        </div>
      </section>

      {!showApplicationForm ? (
        <>
          {/* Why Work With Us */}
          <section className="container mx-auto px-6 py-16">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Solutions Healthcare?</h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Join a team that values professional growth, work-life balance, and patient-centered care.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8 mb-16">
                <div className="bg-white rounded-xl p-8 shadow-md border border-gray-100">
                  <div className="w-16 h-16 bg-[rgba(75,162,75,0.1)] rounded-full flex items-center justify-center mb-6">
                    <i className="ri-heart-line text-3xl text-[rgba(75,162,75)]"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Meaningful Work</h3>
                  <p className="text-gray-600">Make a real impact in patients' lives through compassionate, evidence-based mental healthcare.</p>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-md border border-gray-100">
                  <div className="w-16 h-16 bg-[rgba(75,162,75,0.1)] rounded-full flex items-center justify-center mb-6">
                    <i className="ri-team-line text-3xl text-[rgba(75,162,75)]"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Collaborative Culture</h3>
                  <p className="text-gray-600">Work alongside experienced clinicians in a supportive, team-oriented environment.</p>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-md border border-gray-100">
                  <div className="w-16 h-16 bg-[rgba(75,162,75,0.1)] rounded-full flex items-center justify-center mb-6">
                    <i className="ri-line-chart-line text-3xl text-[rgba(75,162,75)]"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Professional Growth</h3>
                  <p className="text-gray-600">Access continuing education, supervision, and career advancement opportunities.</p>
                </div>
              </div>

              {/* Benefits */}
              <div className="bg-gradient-to-br from-[rgba(75,162,75,0.1)] to-[rgba(75,162,75,0.15)] rounded-xl p-10 border border-[rgba(75,162,75,0.3)] mb-16">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Comprehensive Benefits Package</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-2xl text-[rgba(75,162,75)] mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900">Health Insurance</h4>
                      <p className="text-gray-600 text-sm">Medical, dental, and vision coverage</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-2xl text-[rgba(75,162,75)] mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900">Retirement Plan</h4>
                      <p className="text-gray-600 text-sm">401(k) with employer matching</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-2xl text-[rgba(75,162,75)] mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900">Paid Time Off</h4>
                      <p className="text-gray-600 text-sm">Generous PTO and holiday schedule</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-2xl text-[rgba(75,162,75)] mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900">CEU Support</h4>
                      <p className="text-gray-600 text-sm">Funding for continuing education</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-2xl text-[rgba(75,162,75)] mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900">Flexible Scheduling</h4>
                      <p className="text-gray-600 text-sm">Work-life balance options</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-2xl text-[rgba(75,162,75)] mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900">Malpractice Insurance</h4>
                      <p className="text-gray-600 text-sm">Full coverage provided</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Open Positions */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Current Openings</h3>
                <div className="space-y-6">
                  {openPositions.map((position, index) => (
                    <div key={index} className="bg-white rounded-xl p-8 shadow-md border border-gray-100 hover:shadow-lg transition-all">
                      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                        <div>
                          <h4 className="text-2xl font-bold text-gray-900 mb-2">{position.title}</h4>
                          <span className="inline-block bg-[rgba(75,162,75,0.1)] text-[rgba(75,162,75)] px-4 py-1 rounded-full text-sm font-semibold">
                            {position.type}
                          </span>
                        </div>
                      </div>
                      <p className="text-gray-600 mb-4">{position.description}</p>
                      <div>
                        <h5 className="font-bold text-gray-900 mb-2">Requirements:</h5>
                        <ul className="space-y-1">
                          {position.requirements.map((req, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-gray-600 text-sm">
                              <i className="ri-arrow-right-s-line text-[rgba(75,162,75)] mt-1"></i>
                              <span>{req}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="bg-gradient-to-r from-[rgba(75,162,75)] to-[rgba(75,162,75,0.9)] rounded-xl p-10 text-white text-center">
                <h3 className="text-3xl font-bold mb-4">Ready to Apply?</h3>
                <p className="text-white/80 mb-8 text-lg max-w-2xl mx-auto">
                  Submit your application today and take the first step toward a fulfilling career in mental healthcare.
                </p>
                <button
                  onClick={() => setShowApplicationForm(true)}
                  className="bg-white text-[rgba(75,162,75)] px-10 py-4 rounded-lg font-semibold hover:bg-gray-50 transition-all shadow-lg whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-file-list-3-line mr-2"></i>
                  Apply Now
                </button>
              </div>
            </div>
          </section>
        </>
      ) : (
        <section className="container mx-auto px-6 py-16">
          <div className="max-w-4xl mx-auto">
            <button
              onClick={() => {
                setShowApplicationForm(false);
                setSubmitStatus(null);
              }}
              className="mb-8 text-[rgba(75,162,75)] hover:text-[rgba(75,162,75,0.8)] font-semibold flex items-center gap-2 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Employment Information
            </button>

            <div className="bg-white rounded-xl shadow-lg p-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Employment Application</h2>
              <p className="text-gray-600 mb-8">Complete this form to apply for a position at Solutions Healthcare LLC.</p>

              {submitStatus && (
                <div className={`mb-6 p-4 rounded-lg ${submitStatus.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                  <div className="flex items-center gap-2">
                    <i className={`${submitStatus.type === 'success' ? 'ri-checkbox-circle-fill' : 'ri-error-warning-fill'} text-xl`}></i>
                    <span>{submitStatus.message}</span>
                  </div>
                </div>
              )}

              <form id="employment-application-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">First Name *</label>
                    <input
                      type="text"
                      name="firstName"
                      required
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name *</label>
                    <input
                      type="text"
                      name="lastName"
                      required
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Street Address *</label>
                  <input
                    type="text"
                    name="address"
                    required
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                  />
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">City *</label>
                    <input
                      type="text"
                      name="city"
                      required
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">State *</label>
                    <input
                      type="text"
                      name="state"
                      required
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">ZIP Code *</label>
                    <input
                      type="text"
                      name="zipCode"
                      required
                      value={formData.zipCode}
                      onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Position Applied For *</label>
                    <select
                      name="positionAppliedFor"
                      required
                      value={formData.positionAppliedFor}
                      onChange={(e) => setFormData({ ...formData, positionAppliedFor: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                    >
                      <option value="">Select Position</option>
                      <option value="Psychiatric Nurse Practitioner">Psychiatric Nurse Practitioner</option>
                      <option value="Licensed Clinical Social Worker">Licensed Clinical Social Worker</option>
                      <option value="Medical Assistant">Medical Assistant</option>
                      <option value="Psychiatry Intern">Psychiatry Intern</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Employment Type *</label>
                    <select
                      name="employmentType"
                      required
                      value={formData.employmentType}
                      onChange={(e) => setFormData({ ...formData, employmentType: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                    >
                      <option value="">Select Type</option>
                      <option value="Full-Time">Full-Time</option>
                      <option value="Part-Time">Part-Time</option>
                      <option value="Internship">Internship</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Available Start Date *</label>
                  <input
                    type="date"
                    name="availableStartDate"
                    required
                    value={formData.availableStartDate}
                    onChange={(e) => setFormData({ ...formData, availableStartDate: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Education *</label>
                  <textarea
                    name="education"
                    required
                    rows={3}
                    maxLength={500}
                    value={formData.education}
                    onChange={(e) => setFormData({ ...formData, education: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent resize-none"
                    placeholder="List your degrees, institutions, and graduation dates (max 500 characters)"
                  ></textarea>
                  <p className="text-sm text-gray-500 mt-1">{formData.education.length}/500 characters</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Relevant Experience *</label>
                  <textarea
                    name="experience"
                    required
                    rows={4}
                    maxLength={500}
                    value={formData.experience}
                    onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent resize-none"
                    placeholder="Describe your relevant work experience (max 500 characters)"
                  ></textarea>
                  <p className="text-sm text-gray-500 mt-1">{formData.experience.length}/500 characters</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Licenses & Certifications *</label>
                  <textarea
                    name="certifications"
                    required
                    rows={3}
                    maxLength={500}
                    value={formData.certifications}
                    onChange={(e) => setFormData({ ...formData, certifications: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent resize-none"
                    placeholder="List all relevant licenses and certifications (max 500 characters)"
                  ></textarea>
                  <p className="text-sm text-gray-500 mt-1">{formData.certifications.length}/500 characters</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Cover Letter *</label>
                  <textarea
                    name="coverLetter"
                    required
                    rows={6}
                    maxLength={500}
                    value={formData.coverLetter}
                    onChange={(e) => setFormData({ ...formData, coverLetter: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent resize-none"
                    placeholder="Tell us why you're interested in this position and what makes you a great fit (max 500 characters)"
                  ></textarea>
                  <p className="text-sm text-gray-500 mt-1">{formData.coverLetter.length}/500 characters</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Resume Upload *</label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <i className="ri-file-upload-line text-4xl text-gray-400 mb-2"></i>
                    <p className="text-gray-600 mb-2">Resume uploads are not collected through this form</p>
                    <p className="text-sm text-gray-500">Please email your resume to: careers@solutionshealthcare.com</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Professional References</label>
                  <textarea
                    name="references"
                    rows={3}
                    maxLength={500}
                    value={formData.references}
                    onChange={(e) => setFormData({ ...formData, references: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent resize-none"
                    placeholder="List 2-3 professional references with contact information (max 500 characters)"
                  ></textarea>
                  <p className="text-sm text-gray-500 mt-1">{formData.references.length}/500 characters</p>
                </div>

                <button
                  type="submit"
                  className="w-full bg-[rgba(75,162,75)] text-white py-4 rounded-lg font-semibold hover:bg-[rgba(75,162,75,0.9)] transition-all shadow-lg whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-send-plane-fill mr-2"></i>
                  Submit Application
                </button>
              </form>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default EmploymentPage;
